import { usePlatformSettingsInfoBannerQuery } from "@/lib/api/requests/platform-settings.requests";
import {
  PlatformInfoBannerSettingsResponseDto,
  PlatformInfoBannerVariant,
} from "@/lib/api/interfaces/platform-settings.interface";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/ui-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/ui-components/ui/dialog";
import { StandardDialogHeader } from "@/util-components/dialog/components/StandardDialogHeader";
import { Context } from "@/util-components/root/context";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Info,
  X,
} from "lucide-react";
import { useContext, useEffect, useState } from "react";

export const DISMISS_STORAGE_PREFIX = "sportypost:global-info-banner:dismissed";

export function clearGlobalInfoBannerDismissStates(): number {
  if (typeof window === "undefined") return 0;

  let count = 0;

  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(DISMISS_STORAGE_PREFIX)) {
      window.localStorage.removeItem(key);
      count++;
      i--;
    }
  }

  return count;
}

const bannerStyles: Record<
  PlatformInfoBannerVariant,
  {
    container: string;
    icon: typeof Info;
    dialogAccent: string;
    detailCard: string;
    detailBadge: string;
    headerVariant: "blue" | "red";
    statusLabel: string;
    cta: string;
    mutedText: string;
  }
> = {
  [PlatformInfoBannerVariant.ERROR]: {
    container: "bg-red-600 border-red-700/80",
    icon: AlertCircle,
    dialogAccent: "bg-red-600",
    detailCard: "border-red-100/70 bg-red-50/60",
    detailBadge: "border-red-200 bg-red-100 text-red-700",
    headerVariant: "red",
    statusLabel: "Fehler",
    cta: "bg-red-600 hover:bg-red-700",
    mutedText: "text-red-50/85",
  },
  [PlatformInfoBannerVariant.INFO]: {
    container: "bg-amber-500 border-amber-600/80",
    icon: Info,
    dialogAccent: "bg-amber-500",
    detailCard: "border-amber-100/80 bg-amber-50/60",
    detailBadge: "border-amber-200 bg-amber-100 text-amber-700",
    headerVariant: "blue",
    statusLabel: "Info",
    cta: "bg-blue-600 hover:bg-blue-700",
    mutedText: "text-amber-50/85",
  },
  [PlatformInfoBannerVariant.SUCCESS]: {
    container: "bg-emerald-600 border-emerald-700/80",
    icon: CheckCircle2,
    dialogAccent: "bg-emerald-600",
    detailCard: "border-emerald-100/80 bg-emerald-50/60",
    detailBadge: "border-emerald-200 bg-emerald-100 text-emerald-700",
    headerVariant: "blue",
    statusLabel: "Erfolg",
    cta: "bg-blue-600 hover:bg-blue-700",
    mutedText: "text-emerald-50/85",
  },
};

function buildDismissStorageKey(banner: PlatformInfoBannerSettingsResponseDto): string {
  const version = banner.updatedAt ?? `${banner.variant}:${banner.title}:${banner.content}`;
  return `${DISMISS_STORAGE_PREFIX}:${version}`;
}

export function GlobalInfoBannerHost() {
  const { user } = useContext(Context);
  const [isDismissed, setIsDismissed] = useState(false);
  const { data } = usePlatformSettingsInfoBannerQuery({
    enabled: Boolean(user),
  });

  useEffect(() => {
    if (!data?.enabled) {
      setIsDismissed(false);
      return;
    }

    setIsDismissed(window.localStorage.getItem(buildDismissStorageKey(data)) === "1");
  }, [data]);

  if (!user || !data?.enabled || !data.title.trim() || !data.content.trim() || isDismissed) {
    return null;
  }

  return (
    <GlobalInfoBanner
      banner={data}
      onDismiss={() => {
        window.localStorage.setItem(buildDismissStorageKey(data), "1");
        setIsDismissed(true);
      }}
    />
  );
}

export function GlobalInfoBanner({
  banner,
  onDismiss,
  dismissible = true,
  previewLabel,
}: {
  banner: PlatformInfoBannerSettingsResponseDto;
  onDismiss?: () => void;
  dismissible?: boolean;
  previewLabel?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const style = bannerStyles[banner.variant];
  const Icon = style.icon;

  return (
    <>
      <div className={cn("w-full border-b shadow-sm", style.container)}>
        <div className="px-4 sm:px-6">
          <div className="mx-auto flex w-full max-w-7xl items-center gap-3 py-2.5 text-white">
            <Icon className="h-4 w-4 shrink-0" />

            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
            >
              <span className="truncate text-sm font-semibold tracking-tight">{banner.title}</span>
              {previewLabel ? (
                <span className="hidden rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.14em] sm:inline-flex">
                  {previewLabel}
                </span>
              ) : null}
              <span className={cn("hidden text-xs sm:inline", style.mutedText)}>
                Details anzeigen
              </span>
              <ChevronRight className="hidden h-4 w-4 sm:inline" />
            </button>

            {dismissible ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(event) => {
                  event.stopPropagation();
                  onDismiss?.();
                }}
                className="h-8 w-8 shrink-0 rounded-full text-white hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-2xl p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0">
          <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
            <StandardDialogHeader
              icon={Icon}
              title={banner.title}
              variant={style.headerVariant}
              className="p-0 border-none"
            />
          </div>

          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2 space-y-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5">
                <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{banner.content}</p>
              </div>
            </div>
          </div>

          <DialogFooter className="px-5 sm:px-6 py-4 shrink-0 border-t border-gray-100 bg-gradient-to-t from-background via-background to-background/95 !flex-col gap-3 sm:!flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex w-full sm:w-auto">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsOpen(false)}
                className="h-11 w-full justify-center rounded-xl px-4 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900 sm:w-auto sm:justify-start"
              >
                Schließen
              </Button>
            </div>
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <Button
                type="button"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "h-11 w-full px-6 sm:px-8 min-w-[140px] rounded-xl font-bold text-sm text-white shadow-sm transition-all sm:w-auto",
                  style.cta,
                )}
              >
                Verstanden
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
